import React, { useContext, useState } from "react";
import './ProductDisplay.css';
import { ShopContext } from "../../context/ShopContext";
import { formatPrice } from "../../utils/formatPrice";
import { SHEET_EXTRA_LBS } from "../../utils/sheetPricing";
import ToggleGroup from "./ToggleGroup";
import StandardCakeOptions from "./cake-options/StandardCakeOptions";
import StepCakeOptions from "./cake-options/StepCakeOptions";
import ThemedCakeOptions from "./cake-options/ThemedCakeOptions";
import StarRating from "./StarRating";

const LB_OPTIONS = [1.5, 2, 2.5, 4];
const THEMED_MIN_LBS = 2.5;
const MIN_SLICE_PIECES = 10;
// An additional layer costs LAYER_COST_RATIO of the cake's current base price
// (egg or eggless rate, at whichever weight/sheet size is selected).
const LAYER_COST_RATIO = 0.20;

const CAKE_OPTIONS_BY_TYPE = {
    "step-cake": StepCakeOptions,
    themed: ThemedCakeOptions,
};

const CakeProductPage = (props) => {
    const { product, averageRating = 0, reviewCount = 0 } = props;
    const { addToCart } = useContext(ShopContext);

    const hasVariants = Array.isArray(product.variants) && product.variants.length > 0;
    const eggTypes = hasVariants ? [...new Set(product.variants.map((v) => v.eggType))] : [];
    const [selectedEggType, setSelectedEggType] = useState(eggTypes[0]);

    const availableFormats = hasVariants
        ? product.variants.filter((v) => v.eggType === selectedEggType).map((v) => v.format)
        : [];
    const [selectedFormat, setSelectedFormat] = useState(availableFormats[0]);

    const baseVariant = hasVariants
        ? product.variants.find((v) => v.eggType === selectedEggType && v.format === selectedFormat)
            || product.variants.find((v) => v.eggType === selectedEggType)
        : null;

    // Step cakes and themed cakes only offer Egg/Eggless + Layer/Step count -
    // no format choice (always full cake), no weight, no sheet.
    const isStepCake = product.type === "step-cake";
    const isThemed = product.type === "themed";
    const isMinimalCake = isStepCake || isThemed;

    // Sheet size (quarter/half) is offered on every cake except themed and
    // step-cake. Rasmalai/Guava "Sheet" products are sheet-only - no
    // round/lbs option.
    const sheetEligible = !!product.sheetEligible;
    const [cakeStyle, setCakeStyle] = useState("round");
    const [sheetSize, setSheetSize] = useState("quarter");
    const usingSheet = !!product.sheetOnly || (sheetEligible && cakeStyle === "sheet");

    // Weight (lbs) is a round-cake-only option - sheet cakes are sized by
    // sheet size instead. Themed cakes start at 2.5 lb, not 1.5 lb.
    const lbOptions = product.type === "themed" ? LB_OPTIONS.filter((v) => v >= THEMED_MIN_LBS) : LB_OPTIONS;
    const [selectedLbs, setSelectedLbs] = useState(lbOptions[0]);

    // There's no flat "full" price - the round-cake price is always
    // rate * selectedLbs, computed live (not a surcharge on top of a fixed
    // base). Eggless uses its own (higher) rate throughout - weight, sheet
    // surcharge, and layer cost all key off whichever egg type is selected.
    const eggTypeLbRate = selectedEggType === "egg" ? product.lbRate : product.egglessLbRate;

    const MIN_LAYERS = isThemed ? 4 : 2;
    const MAX_LAYERS = isThemed ? 6 : 3;
    const [selectedLayers, setSelectedLayers] = useState(MIN_LAYERS);

    const lbsApply = selectedFormat === "full" && !usingSheet && !isMinimalCake && product.lbRate != null;

    // Boxed slices are sold in a box of MIN_SLICE_PIECES minimum - price is
    // per-slice price x number of pieces, no minimum-order surcharge.
    const piecesApply = selectedFormat === "slice";
    const [selectedPieces, setSelectedPieces] = useState(MIN_SLICE_PIECES);

    const roundPrice = lbsApply ? eggTypeLbRate * selectedLbs : null;
    // Sheet surcharge uses the same lbRate delta formula, but with a fixed
    // lb-equivalent per size instead of a selectable weight. Only applies
    // to the full/sheet purchase, not a per-slice order.
    const sheetSurcharge = usingSheet && selectedFormat === "full" ? eggTypeLbRate * SHEET_EXTRA_LBS[sheetSize] : 0;

    // What the cake currently costs before any layer surcharge - the round
    // price at the selected weight, the sheet price at the selected size, or
    // the fixed base for step/themed cakes (no weight/sheet selection).
    const currentBasePrice = roundPrice != null
        ? roundPrice
        : usingSheet
            ? (baseVariant?.price ?? 0) + sheetSurcharge
            : baseVariant?.price;

    // An additional layer/step costs LAYER_COST_RATIO of the cake's current
    // base price - so it scales with whichever weight/sheet size is
    // selected, not just egg vs eggless. For step cakes this is the
    // extra-step cost - it's just not displayed as a "+$" delta in the Step
    // toggle (see StepCakeOptions).
    const effectiveLayerPrice = currentBasePrice != null ? currentBasePrice * LAYER_COST_RATIO : null;
    const layersApply = selectedFormat === "full" && effectiveLayerPrice != null && !product.noLayers;

    const layerSurcharge = layersApply ? (selectedLayers - MIN_LAYERS) * effectiveLayerPrice : 0;
    const basePrice = piecesApply
        ? baseVariant?.price * selectedPieces
        : currentBasePrice;
    const selectedVariant = baseVariant
        ? {
            ...baseVariant,
            price: basePrice + layerSurcharge,
            layers: layersApply ? selectedLayers : MIN_LAYERS,
            lbs: lbsApply ? selectedLbs : undefined,
            sheetSize: usingSheet ? sheetSize : undefined,
            pieces: piecesApply ? selectedPieces : undefined,
        }
        : null;

    const displayPrice = hasVariants ? selectedVariant?.price : product.new_price;

    const images = product.images && product.images.length > 0 ? product.images : [product.image];
    const [mainImage, setMainImage] = useState(images[0]);

    const handleEggTypeChange = (eggType) => {
        setSelectedEggType(eggType);
        const formatsForEgg = product.variants.filter((v) => v.eggType === eggType).map((v) => v.format);
        if (!formatsForEgg.includes(selectedFormat)) {
            setSelectedFormat(formatsForEgg[0]);
        }
    };

    const OptionsComponent = CAKE_OPTIONS_BY_TYPE[product.type] || StandardCakeOptions;

    return (
        <>
            <div className="productdisplay w-full">

                <div className="productdisplay-left">
                    <div className="productdisplay-img-list">
                        {images.map((img, i) => (
                            <img
                                key={i}
                                src={img}
                                alt=""
                                className="productdisplay-thumb-img"
                                style={{ cursor: "pointer", border: img === mainImage ? "2px solid #f4511e" : undefined }}
                                onClick={() => setMainImage(img)}
                            />
                        ))}
                    </div>
                    <div className="productdisplay-img">
                        <img src={mainImage} alt="" className="productdisplay-main-img" />
                    </div>
                </div>

                <div className="productdisplay-right">
                    <span className="product-name">{product.name}</span>
                    <div className="productdisplay-right-star">
                        <StarRating rating={averageRating} />
                        <p>({reviewCount})</p>
                    </div>
                    <div className="productdisplay-right-prices">
                        <div className="productdisplay-right-price-new">
                            {formatPrice(displayPrice)}
                        </div>
                    </div>
                    {product.note && <p className="productdisplay-note">{product.note}</p>}

                    {hasVariants && (
                        <>
                            <ToggleGroup
                                label="Egg / Eggless"
                                options={eggTypes}
                                selected={selectedEggType}
                                onSelect={handleEggTypeChange}
                                formatOption={(v) => (v === "egg" ? "Egg" : "Eggless")}
                            />
                            {!isMinimalCake && (
                                <OptionsComponent
                                    product={product}
                                    selectedFormat={selectedFormat}
                                    onFormatChange={setSelectedFormat}
                                    availableFormats={availableFormats}
                                    piecesApply={piecesApply}
                                    selectedPieces={selectedPieces}
                                    onPiecesChange={setSelectedPieces}
                                    baseVariant={baseVariant}
                                    sheetEligible={sheetEligible}
                                    cakeStyle={cakeStyle}
                                    onCakeStyleChange={setCakeStyle}
                                    usingSheet={usingSheet}
                                    sheetSize={sheetSize}
                                    onSheetSizeChange={setSheetSize}
                                    eggTypeLbRate={eggTypeLbRate}
                                    lbsApply={lbsApply}
                                    lbOptions={lbOptions}
                                    selectedLbs={selectedLbs}
                                    onLbsChange={setSelectedLbs}
                                    layersApply={layersApply}
                                    selectedLayers={selectedLayers}
                                    onLayersChange={setSelectedLayers}
                                    MIN_LAYERS={MIN_LAYERS}
                                    MAX_LAYERS={MAX_LAYERS}
                                    effectiveLayerPrice={effectiveLayerPrice}
                                />
                            )}
                            {isMinimalCake && (
                                <OptionsComponent
                                    layersApply={layersApply}
                                    selectedLayers={selectedLayers}
                                    onLayersChange={setSelectedLayers}
                                    MIN_LAYERS={MIN_LAYERS}
                                    MAX_LAYERS={MAX_LAYERS}
                                    effectiveLayerPrice={effectiveLayerPrice}
                                />
                            )}
                        </>
                    )}

                    <button onClick={() => { addToCart(product, selectedVariant) }} >ADD TO CART</button>
                    <p className="productdisplay-right-category"><span>Category: </span>{product.category}</p>
                </div>

            </div >
        </>
    )
}

export default CakeProductPage;
